import React, { useCallback, useMemo, useState } from 'react';
import { View, Pressable, FlatList } from 'react-native';
import { Feather, FontAwesome } from '@expo/vector-icons';
import { firestore } from '../../services/firebase';
import { useAuthUser } from '../../contexts/useAuthUser';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  Container,
  Content,
  Header,
  CountBadge,
  ReplyingBanner,
  EmptyState,
} from './style';
import { StandardText, SmallText, FooterText } from '../../config/theme/globalStyles';
import THEME from '../../config/theme';
import useViewport from '../../hooks/useViewport';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { hexToRgba } from '../../utils/colorUtils';
import BrandInput from '../BrandInput';
import CommentItem from './CommentItem';
import CommentAvatar from './CommentAvatar';
import CommentSkeleton from './CommentSkeleton';
import { useCommentTypography } from './useCommentTypography';

const MAX_COMMENT_LENGTH = 1000;

const VideoComments = ({ login, comments, videoId, categoryId, cardId }) => {
  const { width } = useViewport();
  const breakpoint = 1080;
  const isDesktop = width >= breakpoint;
  const { customColor } = useCustomTheme();

  // Sessão já resolvida no boot; `authUser` traz o uid e `currentUser` o
  // documento `users/{uid}` — usado para gravar o autor junto do comentário.
  const { authUser, user: currentUser, isLogged } = useAuthUser();
  const user = isLogged ? authUser : null;

  const [newComment, setNewComment] = useState('');
  const [newReply, setNewReply] = useState('');
  const [isReply, setIsReply] = useState(false);
  const [commentId, setCommentId] = useState('');
  const [replyingTo, setReplyingTo] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const textColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const titleColor = customColor?.text?.app || THEME.COLORS.TITLE_MAIN;
  const accent = customColor?.primary || THEME.COLORS.PRIMARY_900;
  const background = customColor?.background?.app || THEME.COLORS.BACKGROUND_MAIN;
  const buttonBackground = customColor?.button?.backgroundApp || accent;
  const buttonText = customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON;

  const divider = hexToRgba(textColor, 0.08) || '#E5E7EB';
  const surface = hexToRgba(textColor, 0.05) || '#F3F4F6';
  const muted = hexToRgba(textColor, 0.6) || '#6B7280';

  const { fontTitle, fontText, fontMD, fontSM, fontXS } = useCommentTypography();

  // `undefined` = ainda não chegou o primeiro snapshot (mostra esqueleto);
  // `[]` = não há comentários (mostra estado vazio). Antes os dois eram `[]` e
  // era por isso que existia um setTimeout de 500ms fingindo carregamento.
  const isLoadingComments = comments === undefined || comments === null;
  const commentList = isLoadingComments ? [] : comments;

  const totalCount = useMemo(
    () =>
      commentList.reduce(
        (total, comment) => total + 1 + (comment.replies?.length || 0),
        0
      ),
    [commentList]
  );

  const changeCommentPlaceholder = useCallback((text, value, authorName) => {
    setIsReply(true);
    setCommentId(value);
    setReplyingTo(authorName || '');
    setSendError('');
  }, []);

  // Sem isto não havia como sair do modo resposta: depois de responder uma vez,
  // TODO comentário seguinte virava resposta daquele mesmo comentário.
  const cancelReply = useCallback(() => {
    setIsReply(false);
    setCommentId('');
    setReplyingTo('');
    setNewReply('');
  }, []);

  // Grava o autor junto do comentário (nome, foto e se é admin). A leitura não
  // depende mais de conseguir ler `users/{outroUid}`, o que permite fechar esse
  // documento — que carrega e-mail, celular e compras — sem esvaziar a lista.
  const authorFields = () => ({
    authorName: currentUser?.Nome_Completo || authUser?.displayName || null,
    authorPhoto: currentUser?.photo || null,
    authorIsAdmin: currentUser?.isAdmin === true,
  });

  const sendComment = async () => {
    const text = newComment.trim();
    if (!user || !text || isSending) return;

    try {
      setIsSending(true);
      setSendError('');

      await addDoc(
        collection(
          firestore,
          'categories',
          categoryId,
          'cards',
          cardId,
          'videos',
          videoId,
          'comments'
        ),
        {
          comment: text,
          userId: user.uid,
          // Relógio do servidor, não o da máquina do aluno: com `new Date()` um
          // relógio adiantado fixava o comentário no topo do `orderBy` para
          // sempre, e um atrasado o enterrava no fim da lista.
          createdAt: serverTimestamp(),
          ...authorFields(),
        }
      );

      setNewComment('');
    } catch (error) {
      console.error('Erro ao enviar comentário:', error);
      setSendError('Não foi possível enviar. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  const sendReply = async () => {
    const text = newReply.trim();
    if (!user || !text || !commentId || isSending) return;

    try {
      setIsSending(true);
      setSendError('');

      await addDoc(
        collection(
          firestore,
          'categories',
          categoryId,
          'cards',
          cardId,
          'videos',
          videoId,
          'comments',
          commentId,
          'replies'
        ),
        {
          comment: text,
          userId: user.uid,
          createdAt: serverTimestamp(),
          ...authorFields(),
        }
      );

      cancelReply();
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
      setSendError('Não foi possível enviar. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = () => (isReply ? sendReply() : sendComment());

  const handleDeleteComment = async (idToDelete) => {
    const commentRef = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      videoId,
      'comments',
      idToDelete
    );

    try {
      const repliesSnapshot = await getDocs(collection(commentRef, 'replies'));
      await Promise.all(
        repliesSnapshot.docs.map((replyDoc) => deleteDoc(replyDoc.ref))
      );
      await deleteDoc(commentRef);
    } catch (error) {
      console.error('Erro ao excluir comentário:', error);
    }
  };

  const handleDeleteReplyCard = async (parentCommentId, replyId) => {
    const replyRef = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      videoId,
      'comments',
      parentCommentId,
      'replies',
      replyId
    );

    try {
      await deleteDoc(replyRef);
    } catch (error) {
      console.error('Erro ao excluir resposta:', error);
    }
  };

  const isAdminOrIsUserCurrent = (currentAuthUser, isAdmin, id) => {
    return currentAuthUser?.uid === id || isAdmin === true;
  };

  const renderItem = useCallback(
    ({ item }) => (
      <CommentItem
        handleDeleteComment={handleDeleteComment}
        login={login}
        comment={item}
        editPlaceholder={changeCommentPlaceholder}
        isAdminOrIsUserCurrent={isAdminOrIsUserCurrent}
        userId={user}
        handleDeleteReplyCard={handleDeleteReplyCard}
      />
    ),
    [login, user, changeCommentPlaceholder]
  );

  const draft = isReply ? newReply : newComment;
  const canSend = !!draft.trim() && !isSending;

  const handleKeyPress = (event) => {
    // Enter envia, Shift+Enter quebra linha — o padrão de qualquer caixa de
    // comentário. Só faz sentido no teclado físico (web).
    if (event?.nativeEvent?.key !== 'Enter' || event?.nativeEvent?.shiftKey) return;
    event.preventDefault?.();
    if (canSend) handleSend();
  };

  return (
    <Container scrolls={isDesktop}>
      <Header>
        <StandardText
          color={titleColor}
          style={{ fontFamily: fontTitle, fontSize: fontMD }}
          textAlign="left"
        >
          Comentários
        </StandardText>

        {totalCount > 0 && (
          <CountBadge style={{ backgroundColor: surface }}>
            <FooterText
              color={muted}
              style={{ fontFamily: fontText, fontSize: fontXS }}
            >
              {totalCount}
            </FooterText>
          </CountBadge>
        )}
      </Header>

      <View
        style={{
          height: 1,
          backgroundColor: divider,
          marginTop: 12,
          marginBottom: 4,
        }}
      />

      {isLoadingComments ? (
        <CommentSkeleton compact={!isDesktop} />
      ) : totalCount === 0 ? (
        <EmptyState scrolls={isDesktop}>
          <Feather
            name="message-circle"
            size={28}
            color={hexToRgba(textColor, 0.25) || '#D1D5DB'}
            style={{ marginBottom: 10 }}
          />
          <SmallText
            color={textColor}
            style={{ fontFamily: fontTitle, fontSize: fontSM }}
          >
            Nenhum comentário ainda
          </SmallText>
          <FooterText
            color={muted}
            style={{ fontFamily: fontText, fontSize: fontXS, marginTop: 4 }}
          >
            {user
              ? 'Seja o primeiro a comentar esta aula.'
              : 'Entre na sua conta para comentar.'}
          </FooterText>
        </EmptyState>
      ) : isDesktop ? (
        // DESKTOP: a coluna ao lado do vídeo tem altura fixa, então a lista rola
        // por conta própria enquanto o vídeo fica parado.
        <FlatList
          data={commentList}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          style={{ flex: 1 }}
          showsVerticalScrollIndicator
        />
      ) : (
        // CELULAR: quem rola é a página. Aqui os itens são empilhados numa View
        // comum, sem FlatList.
        //
        // Não adianta usar a FlatList com `scrollEnabled={false}`: ela continua
        // sendo um ScrollView e o react-native-web marca esse nó com
        // `touch-action: none` — o dedo arrastando sobre os comentários não
        // rolaria nada, nem eles nem a página.
        <View>
          {commentList.map((item) => (
            <React.Fragment key={item.id}>{renderItem({ item })}</React.Fragment>
          ))}
        </View>
      )}

      {isReply && user && (
        <ReplyingBanner style={{ backgroundColor: surface, marginTop: 12 }}>
          <FooterText
            color={muted}
            style={{ fontFamily: fontText, fontSize: fontXS, flex: 1 }}
            textAlign="left"
            numberOfLines={1}
          >
            {replyingTo ? `Respondendo a ${replyingTo}` : 'Respondendo'}
          </FooterText>
          <Pressable
            onPress={cancelReply}
            accessibilityRole="button"
            accessibilityLabel="Cancelar resposta"
            hitSlop={12}
          >
            <Feather name="x" size={14} color={muted} />
          </Pressable>
        </ReplyingBanner>
      )}

      {user ? (
        <Content style={{ borderTopColor: divider, backgroundColor: background }}>
          <CommentAvatar
            size={32}
            photo={currentUser?.photo}
            label="Sua foto de perfil"
          />
          <BrandInput
            placeholder={isReply ? 'Escreva sua resposta' : 'Faça um comentário'}
            value={draft}
            onChangeText={isReply ? setNewReply : setNewComment}
            onKeyPress={handleKeyPress}
            maxLength={MAX_COMMENT_LENGTH}
            multiline
            editable={!isSending}
            style={{
              flex: 1,
              minHeight: 44,
              maxHeight: 120,
              marginLeft: 10,
              marginRight: 8,
            }}
          />
          <Pressable
            onPress={handleSend}
            disabled={!canSend}
            accessibilityRole="button"
            accessibilityLabel={isReply ? 'Enviar resposta' : 'Enviar comentário'}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: buttonBackground,
              opacity: canSend ? 1 : 0.4,
            }}
          >
            <FontAwesome name="send" size={15} color={buttonText} />
          </Pressable>
        </Content>
      ) : (
        <Content style={{ borderTopColor: divider, backgroundColor: background }}>
          <FooterText
            color={muted}
            style={{ fontFamily: fontText, fontSize: fontXS, paddingVertical: 10 }}
            textAlign="left"
          >
            Entre na sua conta para comentar.
          </FooterText>
        </Content>
      )}

      {!!sendError && (
        <FooterText
          color={THEME.COLORS.ALERT}
          style={{ fontFamily: fontText, fontSize: fontXS, marginTop: 6 }}
          textAlign="left"
        >
          {sendError}
        </FooterText>
      )}
    </Container>
  );
};

export default VideoComments;
